import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="table-filter"
export default class extends Controller {
  static targets = [ "tableContainer" ]

  connect() {
    this.table = this.tableContainerTarget.querySelector('table');
    if (!this.table) {
        console.error("Table not found within the target container.");
        return;
    }
    // Initial state is "Show All" (switch unchecked)
    this._showAllRows();
    
    // Set initial filter state
    this.currentFilter = 'all';
    this.isToggleActive = false;
  }

  // New method for radio button filters
  setFilter(event) {
    if (!this.table) return;
    
    this.currentFilter = event.target.value;
    this._applyCurrentFilter();
  }

  // Existing toggle filter method - updated to work with new system
  toggleFilter(event) {
    if (!this.table) return;
    
    this.isToggleActive = event.target.checked;
    this._applyCurrentFilter();
  }

  _applyCurrentFilter() {
    if (this.isToggleActive) {
      // Legacy toggle: show only danger/critical rows
      this._showDangerRows();
    } else {
      // Use radio button filter
      switch(this.currentFilter) {
        case 'critical':
          this._showCriticalRows();
          break;
        case 'non-critical':
          this._showNonCriticalRows();
          break;
        case 'clean':
          this._showCleanRows();
          break;
        default:
          this._showAllRows();
      }
    }
  }

  _showAllRows() {
    const rows = this.table.querySelectorAll('tbody tr');
    rows.forEach(row => {
      row.style.display = '';
      row.classList.remove('filtered-out');
    });
  }

  _showCriticalRows() {
    const rows = this.table.querySelectorAll('tbody tr');
    rows.forEach(row => {
      const hasCriticalAlert = this._hasCriticalAlert(row);
      if (hasCriticalAlert) {
        row.style.display = '';
        row.classList.remove('filtered-out');
        row.classList.add('alert-critical');
      } else {
        row.style.display = 'none';
        row.classList.add('filtered-out');
        row.classList.remove('alert-critical');
      }
    });
  }

  _showNonCriticalRows() {
    const rows = this.table.querySelectorAll('tbody tr');
    rows.forEach(row => {
      const hasNonCriticalAlert = this._hasNonCriticalAlert(row);
      if (hasNonCriticalAlert) {
        row.style.display = '';
        row.classList.remove('filtered-out');
        row.classList.add('alert-non-critical');
      } else {
        row.style.display = 'none';
        row.classList.add('filtered-out');
        row.classList.remove('alert-non-critical');
      }
    });
  }

  _showCleanRows() {
    const rows = this.table.querySelectorAll('tbody tr');
    rows.forEach(row => {
      const isClean = this._isCleanRow(row);
      if (isClean) {
        row.style.display = '';
        row.classList.remove('filtered-out');
        row.classList.add('alert-clean');
      } else {
        row.style.display = 'none';
        row.classList.add('filtered-out');
        row.classList.remove('alert-clean');
      }
    });
  }

  // Legacy method for backward compatibility
  _showDangerRows() {
     const rows = this.table.querySelectorAll('tbody tr');
     rows.forEach(row => {
        const hasDangerCell = row.querySelector('td.bg-danger');
        const hasCriticalAlert = this._hasCriticalAlert(row);
        const hasAlert = hasDangerCell || hasCriticalAlert;
        
        if (hasAlert) {
          row.style.display = '';
          row.classList.remove('filtered-out');
        } else {
          row.style.display = 'none';
          row.classList.add('filtered-out');
        }
    });
  }

  // Helper methods to detect alert types based on cell content
  _hasCriticalAlert(row) {
    const cells = row.querySelectorAll('td');
    return Array.from(cells).some(cell => {
      const content = cell.textContent.toLowerCase();
      const hasQuantityDelta = content.includes('quantity discrepancy') || content.includes('qty delta');
      const hasBookCostDelta = content.includes('book cost discrepancy') || content.includes('book cost delta');
      const hasCriticalBadge = cell.querySelector('.bg-danger');
      return hasQuantityDelta || hasBookCostDelta || hasCriticalBadge;
    });
  }

  _hasNonCriticalAlert(row) {
    const cells = row.querySelectorAll('td');
    return Array.from(cells).some(cell => {
      const content = cell.textContent.toLowerCase();
      const hasMarketValueDelta = content.includes('market value discrepancy') || content.includes('market value delta');
      const hasNonReconciledEntry = content.includes('absent from record') || content.includes('non-reconciled');
      const hasWarningBadge = cell.querySelector('.bg-warning');
      return hasMarketValueDelta || hasNonReconciledEntry || hasWarningBadge;
    });
  }

  _isCleanRow(row) {
    return !this._hasCriticalAlert(row) && !this._hasNonCriticalAlert(row);
  }
} 